import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/features/auth/useAuth';
import { hollowayTheme } from '@/styles/theme';
import { ProtectedLayout } from '@/layouts/ProtectedLayout';
import { PublicLayout } from '@/layouts/PublicLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { withSentryRouting } from '@/lib/sentry';

// Wrap Routes with Sentry instrumentation so transactions carry real route
// names (e.g. /flows/:id) rather than the generic <unknown>.
const SentryRoutes = withSentryRouting(Routes);

// Auth pages
import LoginPage from '@/features/auth/pages/LoginPage';
import RegisterPage from '@/features/auth/pages/RegisterPage';
import ForgotPasswordPage from '@/features/auth/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/features/auth/pages/ResetPasswordPage';
import VerifyEmailPage from '@/features/auth/pages/VerifyEmailPage';
import AcceptInvitePage from '@/features/auth/pages/AcceptInvitePage';
import ChangePasswordPage from '@/features/auth/pages/ChangePasswordPage';

// Protected pages
import DashboardPage from '@/features/dashboard/pages/DashboardPage';
import FlowEditorPage from '@/features/editor/pages/FlowEditorPage';
import VariablesPage from '@/features/variables/pages/VariablesPage';
import RichTextPresetsPage from '@/features/settings/pages/RichTextPresetsPage';
import TemplatesPage from '@/features/dashboard/pages/TemplatesPage';
import SettingsPage from '@/features/settings/pages/SettingsPage';
import { SettingsLayout } from '@/features/settings/SettingsLayout';
import MembersPage from '@/features/members/pages/MembersPage';

// Public pages
import SharedFlowPage from '@/features/shared-flow/pages/SharedFlowPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={hollowayTheme} defaultColorScheme="light">
        <Notifications />
        <AuthProvider>
          <BrowserRouter>
            <SentryRoutes>
              {/* Public auth routes */}
              <Route element={<PublicLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/accept-invite" element={<AcceptInvitePage />} />
              </Route>

              {/* Protected routes */}
              <Route element={<ProtectedLayout />}>
                <Route element={<DashboardLayout />}>
                  <Route path="/dashboard" element={<DashboardPage />} />

                  {/* Library — the building blocks reused while authoring a
                      flow. Grouped apart from Settings because these are
                      everyday authoring tools, not workspace configuration. */}
                  <Route path="/library/variables" element={<VariablesPage />} />
                  <Route path="/library/rich-text-presets" element={<RichTextPresetsPage />} />
                  <Route path="/library/templates" element={<TemplatesPage />} />

                  {/* Settings — workspace configuration, visited rarely.
                      Nested under SettingsLayout so every section gets a trail
                      back to the index rather than being a dead end. */}
                  <Route path="/settings" element={<SettingsLayout />}>
                    <Route index element={<SettingsPage />} />
                    <Route path="members" element={<MembersPage />} />

                    {/* Kept so bookmarks keep resolving after the move into
                        /library. */}
                    <Route
                      path="rich-text-presets"
                      element={<Navigate to="/library/rich-text-presets" replace />}
                    />
                  </Route>

                  <Route path="/variables" element={<Navigate to="/library/variables" replace />} />
                </Route>
                <Route path="/flows/:id" element={<FlowEditorPage />} />
              </Route>

              {/* Forced password change — reachable while authenticated (no layout
                  guard) so it doesn't bounce between PublicLayout and ProtectedLayout. */}
              <Route path="/change-password" element={<ChangePasswordPage />} />

              {/* Public shared flow (no auth required).
                  `/s/` is the canonical short form — the client's telephony
                  system rejects URLs of 56+ characters. `/shared/` is kept
                  permanently so links already in the wild keep resolving. */}
              <Route path="/s/:token" element={<SharedFlowPage />} />
              <Route path="/shared/:token" element={<SharedFlowPage />} />

              {/* Root redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </SentryRoutes>
          </BrowserRouter>
        </AuthProvider>
      </MantineProvider>
    </QueryClientProvider>
  );
}
